/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import Media from 'react-media';
import { makeStyles } from '@material-ui/core';
import {  object } from 'prop-types';
import { CrossMerch } from '../../../../CrossMerchContainer/CrossMerchContainer';
import ScrollbarContainerPDPGrid from '../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainerPDPGrid';
import useSSRMediaQuery from '../../../../../../helpers/hooks/useSSRMediaQuery';

const useStyles = makeStyles((theme) => ({
    content: {
        display: 'grid',
        columnGap: '2rem',
        gridGap: '1rem',
        // Desktop Layout
        gridTemplateColumns: '1fr 1fr',
        gridTemplateAreas: '"productImageContainer productSummaryContainer"',
        maxWidth: '1400px',
        margin: '0 auto',
        '@media screen and (max-width: 1279.9px)': {
            gridTemplateColumns: '40% 60%',
        },
        '@media screen and (max-width: 1023.9px)': {
            // Tablet-ish Layout
            gridTemplateColumns: '40% 60%',
            marginBottom: '40px',
        },
        '@media screen and (max-width: 779.9px)': {
            // Tablet-ish Layout
            gridGap: '1em',
        },
        '@media screen and (max-width: 600px)': {
            // Mobile Layout
            marginBottom: '0px',
        },

    },
    productImageContainer: {
        gridArea: 'productImageContainer',

    },

    productImageContent: {
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridTemplateColumns: '15% 85%',
        gridTemplateAreas: '"productImageAlt productImageGrid"',
        gap: '1rem',
        '@media only screen and (max-width: 1279.9px)': {
            display: 'grid',
            rowGap: '0.5rem',
            gridTemplateRows: '1fr',
            gridTemplateColumns: '100%',
            gridTemplateAreas: `
               "productImageGrid"
               "productImageAlt"
            `,
        },
        '@media only screen and (max-width: 1023.9px)': {
            gap: '10px',
        },
        '& .altImagesContainer': {
            display: 'grid',
            gridAutoRows: 'min-content',
            rowGap: '14px',
            gridArea: 'productImageAlt',
            margin: 0,
        },
    },

    productSummaryContainer: {
        position: 'relative',
        gridArea: 'productSummaryContainer',
        justifySelf: 'end',
        width: 'clamp(320px, 100%, 650px)',
        '@media only screen and (max-width: 1280px)': {
            justifySelf: 'center',
            width: 'clamp(320px, 95%, 650px)',
        },
        '@media only screen and (max-width: 1199px)': {
            justifySelf: 'left',
        },
        '@media screen and (max-width: 600px)': {
            width: '90%',
        },
        '& [data-component="pdpAddToCart"]': {
            '& >div': {
                marginBottom: '0px',
                textAlign: 'center',
            },
        },
        '& .GraphqlFoodProductSkus': {
            margin: '10px 0px 0px 0px',
        },
        '& [data-testid="productName"]': {
            marginBottom: '0px',
        },
    },
    container: {
        '& .MuiExpansionPanelSummary-content': {
            margin: '0px !important',
        },
        '& .MuiExpansionPanelDetails-root': {
            marginLeft: '12px',
            '& ul': {
                paddingLeft: '15px',
            },
        },
        '& .MuiTypography-h6': {
            padding: '7px 0px',
            fontSize: '20px',
            color: theme.palette.ctaButton || '#2F2F2F',
            lineHeight: '30px',
            '& span': {
                top: '9px',
                '& svg': {
                    border: `1px solid ${theme.palette?.ctaButton || '#2F2F2F'}`,
                    borderRadius: '50%',
                },
            },
        },

        '& [data-component="graphqlFoodDetailsUiDesktop"]': {
            marginTop: '3px',
            marginBottom: '0px',
        },
        '& [data-component="foodDetailsExpansionPanel"]': {
            borderBottom: `1px solid ${theme.palette.cms?.productDetailsSummary || '#607767'}`,
            '&.Mui-expanded': {
                borderBottom: `1px solid ${theme.palette.cms?.productDetailsSummary || '#607767'}`,
            },
        },

    },
    bottomContainer: {
        '& [data-component="graphqlFoodDetailsUiMobile"]': {
            marginTop: '3px',
        },
    },
}));

const TwoColPlaRedesign = (props) => {
    const {
        components,
    } = props;

    const {
        ProductImageUI,
        ProductTitle,
        ProductReview,
        GraphqlProductSkus,
        PDPAddToCart,
        NBCUMovieBanner,
        PDPDeliveryPopupContainer,
        ProductDetails,
        PLAProductIcon,
    } = components;
    const classes = useStyles();
    const device = useSSRMediaQuery();
    const { featureFlags = {} } = useContext(CrossMerch);
    const mediaQueries = {
        small: '(max-width: 599px)',
        medium: '(min-width: 600px) and (max-width: 1199px)',
        large: '(min-width: 1200px)',
    };

    const PLAdesktopLayout = () => (
        <div
            className={`${classes.container} ${classes.content}`}
            data-testid="pdp-variant-twoColoumnsFoodPla"
        >
            {/* PRODUCT IMAGE CONTAINER */}
            <div className={`${classes.productImageContainer} productImageContainer`}>
                <ScrollbarContainerPDPGrid
                    contentContainerId="contentRight"
                    isFoodBrand
                    type="productBase"
                >
                    <div className={classes.productImageContent}>
                        {ProductImageUI('grid')}
                    </div>
                </ScrollbarContainerPDPGrid>
            </div>

            {/* PRODUCT SUMMARY */}
            <div className={classes.productSummaryContainer}>
                <div className={classes.productSummaryContent}>
                    {ProductTitle()}
                    {featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && ProductReview()}
                    {GraphqlProductSkus()}
                    {PDPAddToCart()}
                    {ProductDetails()}
                    {NBCUMovieBanner()}
                    {PDPDeliveryPopupContainer()}

                </div>
            </div>
        </div>
    );

    const PLAmobileLayout = () => (
        <>
            {PLAProductIcon()}
            <div
                className={classes.content}
                data-testid="pdp-variant-twoColoumnsFoodPla"
            >
                {/* PRODUCT IMAGE CONTAINER */}
                <div className={`${classes.productImageContainer} productImageContainer`}>
                    <div className={classes.contentLeft}>
                        {ProductImageUI('grid')}
                    </div>
                </div>

                {/* PRODUCT SUMMARY */}
                <div className={classes.productSummaryContainer}>
                    <div className={classes.productSummaryContent}>
                        {ProductTitle()}
                        {GraphqlProductSkus()}
                        {PDPAddToCart()}

                    </div>
                </div>

            </div>
            <div className={`${classes.container} ${classes.bottomContainer}`}>
                {ProductDetails()}
                {NBCUMovieBanner()}
                {PDPDeliveryPopupContainer()}
            </div>
        </>
    );
    return (
        <>
            <Media queries={mediaQueries} defaultMatches={{ small: device === 'mobile', medium: device === 'desktop', large: device === 'desktop' }}>
                {(matches) => (
                    <>
                        {matches.small ? PLAmobileLayout() : PLAdesktopLayout()}
                    </>
                )}

            </Media>
        </>
    );
};

TwoColPlaRedesign.propTypes = {
    components: object.isRequired,
};

export default TwoColPlaRedesign;
